import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import WifiOffOutlinedIcon from "@mui/icons-material/WifiOffOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { IntentStatus } from "../api/types";

interface Props {
  status: IntentStatus | "session_invalid" | "network_error";
  message?: string;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; href: string };
}

type Preset = {
  icon: ReactNode;
  title: string;
  defaultMessage: string;
  tint: string;
  ring: string;
};

const PRESETS: Record<string, Preset> = {
  approved: {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 44, color: "#16A34A" }} />,
    title: "Pago aprobado",
    defaultMessage: "Tu pago se procesó correctamente.",
    tint: "rgba(22, 163, 74, 0.10)",
    ring: "rgba(22, 163, 74, 0.25)",
  },
  pending: {
    icon: <HourglassEmptyIcon sx={{ fontSize: 44, color: "#D97706" }} />,
    title: "Pago en proceso",
    defaultMessage: "Estamos esperando confirmación del banco. Esto puede tardar unos minutos.",
    tint: "rgba(217, 119, 6, 0.10)",
    ring: "rgba(217, 119, 6, 0.25)",
  },
  rejected: {
    icon: <CancelOutlinedIcon sx={{ fontSize: 44, color: "#DC2626" }} />,
    title: "Pago rechazado",
    defaultMessage: "El pago no se pudo procesar. Probá con otro método.",
    tint: "rgba(220, 38, 38, 0.10)",
    ring: "rgba(220, 38, 38, 0.25)",
  },
  session_invalid: {
    icon: <LinkOffOutlinedIcon sx={{ fontSize: 44, color: "#475569" }} />,
    title: "Sesión no disponible",
    defaultMessage:
      "El link no es válido. Verificá la URL o pedí al comercio un nuevo link de pago.",
    tint: "rgba(71, 85, 105, 0.10)",
    ring: "rgba(71, 85, 105, 0.20)",
  },
  network_error: {
    icon: <WifiOffOutlinedIcon sx={{ fontSize: 44, color: "#475569" }} />,
    title: "Error de conexión",
    defaultMessage: "No pudimos comunicarnos con el servidor. Verificá tu red e intentá de nuevo.",
    tint: "rgba(71, 85, 105, 0.10)",
    ring: "rgba(71, 85, 105, 0.20)",
  },
};

export function StatusView({ status, message, primaryAction, secondaryAction }: Props) {
  const preset = PRESETS[status] ?? PRESETS.rejected;
  return (
    <Stack alignItems="center" spacing={2} py={3}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "50%",
          background: preset.tint,
          border: `1px solid ${preset.ring}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {preset.icon}
      </Box>
      <Typography variant="h6">{preset.title}</Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 360 }}>
        {message ?? preset.defaultMessage}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={2}>
        {primaryAction ? (
          <Button variant="contained" onClick={primaryAction.onClick}>
            {primaryAction.label}
          </Button>
        ) : null}
        {secondaryAction ? (
          <Button variant="outlined" href={secondaryAction.href} rel="noopener noreferrer">
            {secondaryAction.label}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
